import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Field from "@/components/intake/Field";
import DateInput from "@/components/DateInput";

const PACK_TYPES = ["bag", "sack", "tub", "bottle", "drum", "jar", "carton", "tray", "unit"];
const UNITS = ["kg", "g", "l", "ml", "unit"];

const blank = {
  material_id: "", supplier_id: "", supplier_batch: "", pack_count: "1", pack_type: "bag",
  pack_size: "", pack_unit: "kg", expiry_date: "", location_id: "",
};

function SimpleSelect({ value, onChange, options, placeholder, testId, className = "h-12" }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={className} data-testid={testId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ScanBanner({ scan, dupCount }) {
  if (!scan) return null;
  return (
    <div className="space-y-2">
      <div
        className={`flex items-start gap-2 rounded-lg px-3 py-2 text-sm ${scan.matched ? "bg-emerald-50 text-emerald-900 border border-emerald-200" : "bg-amber-50 text-amber-900 border border-amber-200"}`}
        data-testid="stocktake-scan-banner"
      >
        {scan.matched ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />}
        <div>
          Scanned <span className="font-mono font-semibold">{scan.scanned_code}</span>
          {scan.matched ? ` · matched ${scan.material_name} from ${scan.supplier_name}. Check the quantity and expiry, then save.` : " · no previous intake found. Pick the material and supplier."}
        </div>
      </div>
      {dupCount > 0 && (
        <div className="text-xs text-amber-700" data-testid="stocktake-duplicate-warning">
          This supplier batch is already in the draft {dupCount}×. Only save it again if this is a separate pack or pallet.
        </div>
      )}
    </div>
  );
}

export default function StockTakeItemDialog({ open, onClose, scan, sessionId, materials, suppliers, locations, defaultLocation, items, onSaved }) {
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!open) return;
    const m = scan?.matched ? scan : null;
    const today = new Date().toISOString().slice(0, 10);
    setForm({
      ...blank,
      supplier_batch: scan?.supplier_batch || scan?.scanned_code || "",
      material_id: m?.material_id || "",
      supplier_id: m?.supplier_id || "",
      pack_count: String(m?.pack_count ?? 1),
      pack_type: m?.pack_type || "bag",
      pack_size: m?.pack_size != null ? String(m.pack_size) : "",
      pack_unit: m?.pack_unit || "kg",
      expiry_date: m?.expiry_date && m.expiry_date >= today ? m.expiry_date : "",
      location_id: m?.location_id || defaultLocation || "",
    });
  }, [open, scan, defaultLocation]);

  const pickMaterial = (id) => {
    const mat = materials.find((x) => x.id === id);
    set({
      material_id: id,
      pack_type: mat?.default_pack_type || form.pack_type,
      pack_size: form.pack_size || (mat?.default_pack_size != null ? String(mat.default_pack_size) : ""),
      pack_unit: mat?.default_pack_unit || mat?.unit || form.pack_unit,
    });
  };

  const total = Number(form.pack_count) * Number(form.pack_size);
  const dupCount = items.filter((i) => form.supplier_batch && i.supplier_batch.toUpperCase() === form.supplier_batch.trim().toUpperCase()).length;

  const save = async () => {
    if (!form.material_id || !form.supplier_id) return toast.error("Pick material and supplier");
    if (!form.supplier_batch.trim()) return toast.error("Supplier batch required");
    if (!(total > 0)) return toast.error("Enter pack count and size per pack");
    if (!form.expiry_date) return toast.error("Enter expiry date");
    setSaving(true);
    try {
      await api.post(`/stock-take/sessions/${sessionId}/items`, {
        scanned_code: scan?.scanned_code || "",
        material_id: form.material_id,
        supplier_id: form.supplier_id,
        supplier_batch: form.supplier_batch.trim(),
        pack_count: Number(form.pack_count),
        pack_type: form.pack_type,
        pack_size: Number(form.pack_size),
        pack_unit: form.pack_unit,
        quantity: total,
        unit: form.pack_unit,
        expiry_date: form.expiry_date,
        location_id: form.location_id || null,
      });
      toast.success(`Added ${total} ${form.pack_unit} to draft`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="stocktake-item-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Count stock item</DialogTitle>
          <DialogDescription>Added to the draft only. Live stock stays the same until you switch over.</DialogDescription>
        </DialogHeader>
        <ScanBanner scan={scan} dupCount={dupCount} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Raw material">
            <SimpleSelect value={form.material_id} onChange={pickMaterial} placeholder="Select material" testId="stocktake-material"
              options={materials.map((m) => ({ value: m.id, label: `${m.name} (${m.unit})` }))} />
          </Field>
          <Field label="Supplier">
            <SimpleSelect value={form.supplier_id} onChange={(v) => set({ supplier_id: v })} placeholder="Select supplier" testId="stocktake-supplier"
              options={suppliers.map((s) => ({ value: s.id, label: s.name }))} />
          </Field>
          <Field label="Supplier batch number">
            <Input className="h-12 font-mono" value={form.supplier_batch} onChange={(e) => set({ supplier_batch: e.target.value })} data-testid="stocktake-supplier-batch" />
          </Field>
          <Field label="Expiry date (DD/MM/YYYY)">
            <DateInput className="h-12" value={form.expiry_date} onChange={(v) => set({ expiry_date: v })} dataTestid="stocktake-expiry" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Quantity counted">
              <div className="grid grid-cols-12 gap-2 items-center">
                <Input type="number" min="0" step="1" inputMode="decimal" className="col-span-3 h-12" value={form.pack_count}
                  onChange={(e) => set({ pack_count: e.target.value })} data-testid="stocktake-pack-count" />
                <SimpleSelect className="col-span-3 h-12" value={form.pack_type} onChange={(v) => set({ pack_type: v })} testId="stocktake-pack-type"
                  options={PACK_TYPES.map((p) => ({ value: p, label: p === "unit" ? p : `${p}s` }))} />
                <span className="col-span-1 text-center text-stone-500">×</span>
                <Input type="number" min="0" step="0.01" inputMode="decimal" className="col-span-3 h-12" value={form.pack_size}
                  onChange={(e) => set({ pack_size: e.target.value })} placeholder="size" data-testid="stocktake-pack-size" />
                <SimpleSelect className="col-span-2 h-12" value={form.pack_unit} onChange={(v) => set({ pack_unit: v })} testId="stocktake-pack-unit"
                  options={UNITS.map((u) => ({ value: u, label: u }))} />
              </div>
              <div className="mt-2 text-xs text-stone-600" data-testid="stocktake-total-preview">
                <span className="uppercase tracking-wider text-[10px] font-bold mr-2">Total</span>
                <span className="font-display font-bold text-base text-primary tnum">
                  {Number.isFinite(total) && total > 0 ? `${+total.toFixed(4)} ${form.pack_unit}` : "—"}
                </span>
              </div>
            </Field>
          </div>
          <Field label="Location">
            <SimpleSelect value={form.location_id || "none"} onChange={(v) => set({ location_id: v === "none" ? "" : v })} testId="stocktake-location"
              options={[{ value: "none", label: "— Any —" }, ...locations.map((l) => ({ value: l.id, label: l.name }))]} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="stocktake-item-cancel">Cancel</Button>
          <Button className="h-12 px-6" onClick={save} disabled={saving} data-testid="stocktake-item-save">
            {saving ? "Saving…" : "Add to draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
